import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { Button, Grid, Stack, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import BadgeDetached from "@entrilia/shared/components/BadgeDetached";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import HorizontalFill from "@entrilia/shared/components/HorizontalFill";
import SearchField from "@entrilia/shared/components/inputs/SearchField";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext.ts";
import useFetch from "@entrilia/shared/hooks/useFetch";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle";
import useRequest from "@entrilia/shared/hooks/useRequest.ts";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../api/adminApi";
import type { Dashboard } from "../../../api/types/dashboardsTypes.ts";
import useClientContext from "../../../context/useClientContext.ts";
import GeneralPageHeader from "../../common/GeneralPageHeader";
import DashboardsList from "./DashboardsList";
import DeleteDashboardsDialog from "./DeleteDashboardsDialog";
import DuplicateDashboardDialog from "./components/DuplicateDashboardDialog";
import CopyToDialog from "./copy/CopyToDialog.tsx";

enum DashboardTypesEnum {
  All = "All",
  CreatedByMe = "CreatedByMe",
  AccessibleToMe = "AccessibleToMe",
}

const CompanyDashboardsPage = () => {
  usePageTitle("Dashboards");
  const navigate = useNavigate();
  const notification = useNotificationContext();
  const { hasAnyPermission } = useClientContext();
  const hasAccessToManageDashboards = useMemo(() => hasAnyPermission(["ManageDashboards"]), [hasAnyPermission]);

  const getDashboards = useCallback((signal?: AbortSignal) => adminApi.getDashboards(signal), []);
  const [dashboards, setDashboards] = useState<Dashboard[]>([]);
  const [, error, { isFetching, fetch: refetchDashboards }] = useFetch(getDashboards, {
    onSetData: (data) => setDashboards(data.dashboards),
  });

  const [searchText, setSearchText] = useState("");
  const [selectedType, setSelectedType] = useState<DashboardTypesEnum>(DashboardTypesEnum.All);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [deleteDashboards, setDeleteDashboards] = useState<Dashboard[]>([]);
  const [duplicateDashboard, setDuplicateDashboard] = useState<Dashboard | undefined>();
  const [copyDashboards, setCopyDashboards] = useState<Dashboard[]>([]);

  const { request: addDashboardDraft, isLoading: addDashboardDraftIsLoading } = useRequest(adminApi.addDashboardDraft);

  const onCloseDeleteDialog = useCallback(() => {
    setDeleteDialogOpen(false);
  }, []);

  const filteredDashboards = useMemo(
    () => dashboards.filter((dashboard) => dashboard.nameUserInput.toLowerCase().includes(searchText.toLowerCase())),
    [dashboards, searchText]
  );

  const myDashboards = [];
  const sharedWithMeDashboards = [];

  const onDashboardCreate = useCallback(async () => {
    const [dashboard, error] = await addDashboardDraft();
    if (error) {
      notification.sendNotificationError("Failed to create dashboard. Please try again.");
      return;
    }
    if (dashboard) {
      navigate(`./${dashboard.dashboard.dashboardId}/edit`);
    }
  }, [addDashboardDraft, navigate, notification]);

  const getDashboardPath = useCallback((dashboardId: string) => `./${dashboardId}/view`, []);

  const onDelete = useCallback(
    (ids: string[]) => {
      setDeleteDialogOpen(true);
      setDeleteDashboards(dashboards.filter((dashboard) => ids.includes(dashboard.dashboardId)));
    },
    [dashboards]
  );

  const onDuplicate = useCallback(
    (id: string) => {
      setDuplicateDialogOpen(true);
      setDuplicateDashboard(dashboards.find((dashboard) => dashboard.dashboardId === id));
    },
    [dashboards]
  );

  const onCopy = useCallback(
    (ids: string[]) => {
      setCopyDialogOpen(true);
      setCopyDashboards(dashboards.filter((dashboard) => ids.includes(dashboard.dashboardId)));
    },
    [dashboards]
  );

  if (error) {
    logError(error, "CompanyDashboardsContainer");
    return <DataLoadingFailed title="Loading dashboards" />;
  }

  return (
    <>
      <GeneralPageHeader title="Dashboards">
        {hasAccessToManageDashboards && (
          <Button
            variant="contained"
            color="primary"
            disabled={addDashboardDraftIsLoading}
            loading={addDashboardDraftIsLoading}
            onClick={onDashboardCreate}
          >
            <AddRoundedIcon />
            Create New
          </Button>
        )}
      </GeneralPageHeader>
      <Stack spacing={2} sx={{ height: "100%", px: 3, pb: 1, pt: 2.5 }}>
        <Grid container sx={{ width: "100%" }}>
          <ToggleButtonGroup
            value={selectedType}
            exclusive
            onChange={(_, value: DashboardTypesEnum) => setSelectedType((prev) => value || prev)}
          >
            <ToggleButton value={DashboardTypesEnum.All} sx={{ gap: 0.5 }}>
              All <BadgeDetached showZero badgeContent={dashboards.length} color="secondary" />
            </ToggleButton>
            <ToggleButton value={DashboardTypesEnum.CreatedByMe} sx={{ gap: 0.5 }}>
              Created by Me
              <BadgeDetached showZero badgeContent={myDashboards.length} color="secondary" />
            </ToggleButton>
            <ToggleButton value={DashboardTypesEnum.AccessibleToMe} sx={{ gap: 0.5 }}>
              Accessible to Me
              <BadgeDetached showZero badgeContent={sharedWithMeDashboards.length} color="secondary" />
            </ToggleButton>
          </ToggleButtonGroup>
          <HorizontalFill />
          <SearchField onSearch={(text) => setSearchText(text.toLowerCase())} debounceTimeMs={300} />
        </Grid>

        <DashboardsList
          dashboards={filteredDashboards}
          isLoading={isFetching}
          getDashboardPath={getDashboardPath}
          onDelete={onDelete}
          onDuplicate={onDuplicate}
          onCopy={onCopy}
        />
      </Stack>
      <DeleteDashboardsDialog
        open={deleteDialogOpen}
        dashboards={deleteDashboards}
        onClose={onCloseDeleteDialog}
        onDeleted={refetchDashboards}
      />
      {duplicateDashboard && (
        <DuplicateDashboardDialog
          open={duplicateDialogOpen}
          dashboard={duplicateDashboard}
          onClose={() => {
            setDuplicateDialogOpen(false);
            setDuplicateDashboard(undefined);
          }}
          onSaved={(newDashboard) => {
            setDashboards([...dashboards, newDashboard]);
            setDuplicateDashboard(undefined);
            setDuplicateDialogOpen(false);
          }}
        />
      )}
      {copyDialogOpen && <CopyToDialog dashboards={copyDashboards} onClose={() => setCopyDialogOpen(false)} />}
    </>
  );
};

export default CompanyDashboardsPage;
